import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../../../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../../../../u2000Core/context/toast/U2000ToastContext';
import {
  doDeleteChildLotChainData,
  luHandleAppiError,
  u3141ParseGraphConcordanceMutationResponse,
  type U3141ChildLotChain,
} from '../../../../U3141Service';
import { NS } from '../../../../U3141';

export type U3141DeleteChildLotChainFromGraphResult = {
  ok: boolean;
  lotChainGraph: U3141ChildLotChain[] | null;
};

/** POST delete child lot chain depuis le graphe — retire le nœud chaîne du registre (BL-0125 / BL-0696), sans supprimer le lot métier ni les documents. */
export function useU3141DeleteChildLotChainFromGraphHandlers(params?: {
  onSuccess?: (result: U3141DeleteChildLotChainFromGraphResult, childLotChain: U3141ChildLotChain) => void;
  onError?: (error: unknown, childLotChain: U3141ChildLotChain) => void;
}) {
  const { onSuccess, onError } = params ?? {};
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const deleteChildLotChainFromGraph = useCallback(
    async (masterLotChainId: string, childLotChain: U3141ChildLotChain): Promise<U3141DeleteChildLotChainFromGraphResult> => {
      const failure: U3141DeleteChildLotChainFromGraphResult = { ok: false, lotChainGraph: null };
      try {
        const res = await doDeleteChildLotChainData(axios, masterLotChainId, childLotChain.id, {
          id: childLotChain.id,
          version: childLotChain.version ?? 0,
        });
        const lotChainGraph = u3141ParseGraphConcordanceMutationResponse(res.data);
        const result: U3141DeleteChildLotChainFromGraphResult = { ok: true, lotChainGraph };
        onSuccess?.(result, childLotChain);
        return result;
      } catch (error: unknown) {
        onError?.(error, childLotChain);
        const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return failure;
      }
    },
    [axios, onSuccess, onError, t, toast],
  );

  return { deleteChildLotChainFromGraph };
}
